export function pickAmount(person, pick) {
    const summ = person.summary?.find(({ name }) => name === pick);
    return summ ? summ.amount : 0;
}

export function lexCompareOutsiders(a, b) {
    for (let i = 0; i < 3; i += 1) {
        const da = a[i] ?? 0;
        const db = b[i] ?? 0;
        if (da !== db) return da - db;
    }
    return 0;
}

/** Higher outsiders tuple first; tie-break by person name. */
export function compareOutsidersRows(a, b) {
    const cmp = lexCompareOutsiders(b.sortedAmountsDesc, a.sortedAmountsDesc);
    if (cmp !== 0) return cmp;
    return a.person.name.localeCompare(b.person.name);
}

/**
 * Where `row` (a leader) first beats `belowRow` on the sorted-desc prize tuple.
 * `firstDiffIndex` 0 = best prize; 1 or 2 = required 2nd/3rd multiset slot to separate.
 */
export function outsidersLeaderEdgeDetail(row, belowRow) {
    if (!belowRow || !row?.lastThree?.length) return null;
    const { person, lastThree } = row;
    const a = row.sortedAmountsDesc;
    const b = belowRow.sortedAmountsDesc;
    if (!a || !b) return null;
    let i = 0;
    while (i < 3 && a[i] === b[i]) i += 1;
    if (i >= 3 || a[i] <= b[i]) return null;
    const target = a[i];
    const picks = lastThree.filter((p) => pickAmount(person, p) === target);
    return { firstDiffIndex: i, picks };
}

/**
 * Picks whose prize equals the first sort key where `row` beats `belowRow`.
 */
export function outsidersEdgePickNames(row, belowRow) {
    const d = outsidersLeaderEdgeDetail(row, belowRow);
    return d?.picks ?? [];
}

/** Rows tied for 1st on outsiders (same sorted-desc prize multiset). */
export function outsidersLeadingGroupSize(ordered) {
    if (!ordered?.length) return 0;
    const r0 = ordered[0];
    const key0 = r0.sortedAmountsDesc;
    let k = 1;
    while (
        k < ordered.length &&
        lexCompareOutsiders(ordered[k].sortedAmountsDesc, key0) === 0
    ) {
        k += 1;
    }
    return k;
}

/**
 * Last 3 picks only: highlight = best prize among them (tie → earliest in list).
 * Sort: lexicographic on sorted-desc amounts (best, 2nd-best, 3rd-best multiset).
 */
export function getLastThreeMeta(person) {
    const lastThree = person.picks.slice(-3);
    if (lastThree.length === 0) {
        return {
            lastThree: [],
            highlightedPick: null,
            sortAmount: 0,
            sortSecondBest: 0,
            sortThirdBest: 0,
            sortedAmountsDesc: [0, 0, 0],
        };
    }

    const amountsInOrder = lastThree.map((pick) => pickAmount(person, pick));
    const sortedDesc = [...amountsInOrder].sort((x, y) => y - x);
    while (sortedDesc.length < 3) sortedDesc.push(0);
    const sortSecondBest = sortedDesc[1] ?? 0;
    const sortThirdBest = sortedDesc[2] ?? 0;

    let highlightedPick = lastThree[0];
    let sortAmount = 0;
    let bestAmount = -1;

    for (const pick of lastThree) {
        const amt = pickAmount(person, pick);
        if (amt > bestAmount) {
            bestAmount = amt;
            highlightedPick = pick;
            sortAmount = amt;
        }
    }

    return {
        lastThree,
        highlightedPick,
        sortAmount,
        sortSecondBest,
        sortThirdBest,
        sortedAmountsDesc: sortedDesc,
    };
}

export function sortRankingsByLastThreeBest(rankings) {
    return [...rankings]
        .map((person) => ({
            person,
            ...getLastThreeMeta(person),
        }))
        .sort(compareOutsidersRows);
}
